import DocsSiteEventType from '@modules/eventStream/enum/DocsSiteEventType';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import { useTranslation } from '@rbx/intl';
import { StudioDialogEventHandlers, StudioDialogParams, useStudio } from '@rbx/studio';
import { TRawClickEvent } from '@rbx/unified-logger';
import { useCallback, useMemo } from 'react';
import { creatorHubBaseUrl } from '@modules/utilities/constants/baseUrl';
import useCurrentLocale from '@modules/localization/hooks/useCurrentLocale';
import { useRobloxAuthentication } from '@rbx/auth';
import studioProtocolScheme from '../constants/studioProtocolScheme';

const downloadStudioOpenOrDownloadEventModel = (downloadCode = ''): TRawClickEvent => ({
  eventName: DocsSiteEventType.DownloadStudio,
  parameters: {
    logic: 'openOrDownload',
    referralUrl: document.referrer,
    downloadCode,
  },
});

const studioStartAttemptEventModel = (task: string): TRawClickEvent => ({
  eventName: DocsSiteEventType.StudioStartAttempt,
  parameters: {
    task: task.toLowerCase(),
    referralUrl: document.referrer,
  },
});

const studioStartSuccessEventModel = (task: string): TRawClickEvent => ({
  eventName: DocsSiteEventType.StudioStartSuccess,
  parameters: {
    task: task.toLowerCase(),
    referralUrl: document.referrer,
  },
});

const useOpenStudio = () => {
  const { translate } = useTranslation();
  const { user: authenticatedUser } = useRobloxAuthentication();
  const { urlLocale } = useCurrentLocale();

  const studioTranslations = {
    'Message.CheckingStudio': translate('Message.CheckingStudio'),
    'Message.StartYourCreation': translate('Message.StartYourCreation'),
    'Message.OpenStudioError': translate('Message.UnknownError'),
    'Action.DownloadStudio': translate('Action.DownloadStudio'),
    'Action.EditInStudio': translate('Action.EditInStudio'),
  };

  const dialogEventHandlers: StudioDialogEventHandlers = {
    startAttempt: (params) =>
      unifiedLoggerClient.logClickEvent(studioStartAttemptEventModel(params.task)),
    startSuccess: (params) =>
      unifiedLoggerClient.logClickEvent(studioStartSuccessEventModel(params.task)),
    download: (_params, helpers?: { getDownloadCode: () => Promise<string | undefined> }) => {
      if (helpers?.getDownloadCode) {
        helpers
          .getDownloadCode()
          .then((downloadCode: string | undefined) => {
            if (downloadCode !== undefined) {
              unifiedLoggerClient.logClickEvent(
                downloadStudioOpenOrDownloadEventModel(downloadCode),
              );
            } else {
              unifiedLoggerClient.logClickEvent(downloadStudioOpenOrDownloadEventModel());
            }
          })
          .catch(() => {
            // Fallback if promise rejects
            unifiedLoggerClient.logClickEvent(downloadStudioOpenOrDownloadEventModel());
          });
      } else {
        // No getDownloadCode helper available
        unifiedLoggerClient.logClickEvent(downloadStudioOpenOrDownloadEventModel());
      }
    },
  };

  const { open, dialog, isCompatible } = useStudio(studioTranslations, dialogEventHandlers);

  const openWrapper = useCallback(
    (params: StudioDialogParams) => {
      open(params, {
        locale: urlLocale,
        protocolScheme: studioProtocolScheme,
        distributorType: 'Global',
        baseUrl: creatorHubBaseUrl,
        userId: authenticatedUser?.id,
      });
    },
    [authenticatedUser?.id, open, urlLocale],
  );

  return useMemo(
    () => ({ open: openWrapper, dialog, isCompatible }),
    [dialog, isCompatible, openWrapper],
  );
};

export default useOpenStudio;
